import { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart,
  ClipboardText,
  Handshake,
  SignOut,
  House,
  MagnifyingGlass,
  User,
  MapPin,
  Calendar,
  Eye
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminApplications = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/applications`);
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredApplications = applications.filter(app => {
    const searchLower = search.toLowerCase();
    return (
      app.user?.name?.toLowerCase().includes(searchLower) ||
      app.user?.location?.toLowerCase().includes(searchLower) ||
      app.user?.email?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Heart weight="fill" className="w-6 h-6 text-primary" />
              <span className="font-serif text-xl text-white">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-stone-400 text-sm">Welcome, {user?.name}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-stone-400 hover:text-white"
              >
                <SignOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-stone-800/50 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-6">
            <Link 
              to="/admin" 
              className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium"
            >
              <House className="w-4 h-4 inline mr-2" />
              Overview
            </Link>
            <Link 
              to="/admin/applications" 
              className="py-4 text-white border-b-2 border-primary text-sm font-medium"
            >
              <ClipboardText className="w-4 h-4 inline mr-2" />
              Applications
            </Link>
            <Link 
              to="/admin/matching" 
              className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium"
            >
              <Handshake className="w-4 h-4 inline mr-2" />
              Matching
            </Link>
            <Link 
              to="/admin/team" 
              className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap"
            >
              Admin Team
            </Link>
            <Link 
              to="/admin/account" 
              className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap"
            >
              My Account
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl text-white mb-2" data-testid="applications-title">
              Applications
            </h1>
            <p className="text-stone-400">
              Review user applications and create matches
            </p>
          </div>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
            <Input
              placeholder="Search by name, email, location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-stone-800 border-stone-700 text-white w-64"
              data-testid="search-applications-input"
            />
          </div>
        </div>

        {/* Applications List */}
        {loading ? (
          <div className="text-stone-400 text-center py-12">Loading applications...</div>
        ) : filteredApplications.length === 0 ? (
          <div className="text-stone-400 text-center py-12">
            {search ? 'No applications match your search' : 'No applications yet'}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((app) => (
              <div 
                key={app.id} 
                className="bg-stone-800 rounded-sm p-6"
                data-testid={`application-${app.id}`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex items-start gap-4">
                    {/* Avatar/Photo */}
                    <div className="w-16 h-16 rounded-sm bg-stone-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {app.photo_base64 ? (
                        <img src={app.photo_base64} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User weight="light" className="w-8 h-8 text-stone-500" />
                      )}
                    </div>
                    
                    {/* Info */}
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-serif text-lg text-white">{app.user?.name}</h3>
                        <Badge variant="outline" className="text-stone-400 border-stone-600">
                          {app.user?.gender}
                        </Badge>
                        <Badge variant="outline" className="text-stone-400 border-stone-600">
                          {app.user?.age} years
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-stone-400 mb-3">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {app.user?.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(app.submitted_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <p className="text-stone-300 text-sm line-clamp-2">
                        {app.about_me || 'No bio provided'}
                      </p>
                      
                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Badge className="bg-stone-700 text-stone-300">
                          {app.relationship_goal || 'Not specified'}
                        </Badge>
                        {app.interests?.slice(0, 3).map((interest, i) => (
                          <Badge key={i} variant="outline" className="border-stone-600 text-stone-400">
                            {interest}
                          </Badge>
                        ))}
                        {app.interests?.length > 3 && (
                          <Badge variant="outline" className="border-stone-600 text-stone-400">
                            +{app.interests.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Link to={`/admin/user/${app.user_id}`}>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-stone-600 text-stone-300 hover:bg-stone-700"
                        data-testid={`view-user-${app.user_id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Full Profile
                      </Button>
                    </Link>
                    <Link to={`/admin/matching?user=${app.user_id}`}>
                      <Button 
                        size="sm"
                        className="btn-primary"
                        data-testid={`match-user-${app.user_id}`}
                      >
                        <Handshake className="w-4 h-4 mr-2" />
                        Find Match
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminApplications;
